import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { clearSearchResults } from '../../redux/slices/searchSlice';
import { fetchSearchProducts } from '../../services';
import type { IProducts } from '../interfaces';
import { useSearchBodyLock } from './useSearchBodyLock';

export const SEARCH_DEBOUNCE_MS = 300;

export const useSearchBar = () => {
  const [query, setQuery] = useState('');
  const [inputOpen, setInputOpen] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [shouldRenderDropdown, setShouldRenderDropdown] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchTimerRef = useRef<number>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const searchState = useAppSelector((state) => state.search);
  const isSearchPanelOpen = dropdownVisible && inputOpen;
  const results = searchState.results;

  useSearchBodyLock(isSearchPanelOpen);

  const cancelScheduledSearch = useCallback(() => {
    if (searchTimerRef.current !== undefined) {
      window.clearTimeout(searchTimerRef.current);
      searchTimerRef.current = undefined;
    }
  }, []);

  const runSearch = useCallback((searchQuery: string) => {
    setDropdownVisible(true);
    dispatch(fetchSearchProducts(searchQuery));
  }, [dispatch]);

  const closeSearch = useCallback(() => {
    cancelScheduledSearch();
    setDropdownVisible(false);
    setInputOpen(false);
    setQuery('');
    dispatch(clearSearchResults());
  }, [cancelScheduledSearch, dispatch]);

  const handleSearch = useCallback(() => {
    const trimmedQuery = query.trim();

    if (!inputOpen) {
      window.dispatchEvent(new CustomEvent('jew:search-open'));
      setHasInteracted(true);
      setInputOpen(true);
      return;
    }

    if (trimmedQuery) {
      cancelScheduledSearch();
      runSearch(trimmedQuery);
      return;
    }

    setDropdownVisible(false);
    dispatch(clearSearchResults());
  }, [cancelScheduledSearch, dispatch, inputOpen, query, runSearch]);

  const handleQueryChange = useCallback((value: string) => {
    setQuery(value);
    cancelScheduledSearch();

    const trimmedQuery = value.trim();
    if (!trimmedQuery) {
      setDropdownVisible(false);
      dispatch(clearSearchResults());
      return;
    }

    searchTimerRef.current = window.setTimeout(() => {
      searchTimerRef.current = undefined;
      runSearch(trimmedQuery);
    }, SEARCH_DEBOUNCE_MS);
  }, [cancelScheduledSearch, dispatch, runSearch]);

  const handleResultClick = useCallback((product: IProducts) => {
    navigate(`/products/${product.id}`, {
      state: {
        selectedMaterial: product.selectedMaterial,
        materialPreview: product.materialPreview,
      },
    });
    window.requestAnimationFrame(closeSearch);
  }, [closeSearch, navigate]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        closeSearch();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [closeSearch]);

  useEffect(() => cancelScheduledSearch, [cancelScheduledSearch]);

  useEffect(() => {
    if (inputOpen) inputRef.current?.focus();
  }, [inputOpen]);

  useEffect(() => {
    if (isSearchPanelOpen) {
      setShouldRenderDropdown(true);
      return undefined;
    }

    const timeoutId = window.setTimeout(() => setShouldRenderDropdown(false), 400);
    return () => window.clearTimeout(timeoutId);
  }, [isSearchPanelOpen]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeSearch();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [closeSearch]);

  return useMemo(() => ({
    closeSearch,
    dropdownVisible,
    handleQueryChange,
    handleResultClick,
    handleSearch,
    hasInteracted,
    inputOpen,
    inputRef,
    isSearchPanelOpen,
    query,
    results,
    searchState,
    shouldRenderDropdown,
    wrapperRef,
  }), [
    closeSearch,
    dropdownVisible,
    handleQueryChange,
    handleResultClick,
    handleSearch,
    hasInteracted,
    inputOpen,
    isSearchPanelOpen,
    query,
    results,
    searchState,
    shouldRenderDropdown,
  ]);
};
